
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/services/LoggingService';
import { Card } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { saveConnectionToDB } from '@/providers/vezgo/utils/database';
import { useVezgoNavigation } from '@/providers/vezgo/hooks/useVezgoNavigation';
import { handleVezgoError } from '@/providers/vezgo/utils/error-handler';
import { useNavigationErrorBoundary } from '@/providers/vezgo/hooks/useNavigationErrorBoundary';
import { toast } from 'sonner';
import { useVezgoState } from '@/providers/vezgo/hooks/useVezgoState';
import { cleanupCallbackParams } from '@/providers/vezgo/utils/cleanup';
import { CallbackProcessor } from '@/providers/vezgo/utils/callback-processor';
import { supabase } from '@/integrations/supabase/client';
import { VEZGO_RETURN_PATH } from '@/providers/vezgo/constants';
import { isValidVezgoAccountId } from '@/providers/vezgo/utils/type-helpers';
import { checkExistingConnection } from '@/providers/vezgo/utils/database';
import { ensureValidToken } from '@/services/AuthAwareApiService';

function VezgoCallback() {
  const navigate = useNavigate();
  const vezgoNavigation = useVezgoNavigation();
  const { handleNavigationError } = useNavigationErrorBoundary();
  const [showRetry, setShowRetry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const vezgoState = useVezgoState();

  
  useEffect(() => {
    let didCancel = false;
    let toastId: string | number | undefined;

    CallbackProcessor.cleanupStaleFlags();

    if (CallbackProcessor.isCallbackRecentlyProcessed()) {
      logger.info('VezgoCallback', 'Already processed callback (skipping)', {
        callbackDetails: CallbackProcessor.getCallbackDetails()
      });
      setTimeout(() => {
        if (!didCancel) {
          navigate('/investments/connect', { replace: true });
        }
      }, 1000);
      return;
    }

    const processCallback = async () => {
      try {
        setIsProcessing(true);
        const params = new URLSearchParams(window.location.search);
        const rawAccount = params.get('account');
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const message = params.get('message');
        
        if (!rawAccount) {
          throw new Error('Missing required account parameter');
        }
        
        // Ensure account ID is always a string
        const accountId = String(rawAccount);
        
        if (!isValidVezgoAccountId(accountId)) {
          throw new Error('Invalid account format in callback');
        }

        const isSuccess = message === 'Success' || !!(accountId && code);

        // Use accountId as string instead of rawAccount
        logger.info('VezgoCallback', 'Processing callback', {
          accountId,
          code,
          isSuccess,
          pathname: window.location.pathname,
          search: window.location.search
        });
        
        // Use the properly typed accountId string
        const existingConnection = await checkExistingConnection(accountId);
        if (existingConnection) {
          logger.info('VezgoCallback', 'Found existing connection', {
            connectionId: existingConnection.id,
            accountId,
            status: existingConnection.connection_status,
            lastConnected: existingConnection.updated_at
          });
        }

        // Fix type error: Convert toastId to string if needed
        const loadingToastId = toast.loading("Processing connection...");
        toastId = loadingToastId;
        
        const provider = (params.get('provider') || 'vezgo') as string;
        
        // Extract client_id from URL params, which follows the Vezgo docs (snake_case in URLs)
        const clientId = params.get('client_id') || null;
        
        const saveResult = await saveConnectionToDB(accountId, {
          code: code || null,
          provider: provider,
          metadata: { 
            lastConnected: new Date().toISOString(),
            accountId: accountId,
            code: code,
            provider: provider,
            clientId: clientId // Store client_id in metadata if available
          }
        });

        if (!saveResult) {
          throw new Error('Failed to save connection to database');
        }

        CallbackProcessor.markCallbackProcessed(accountId);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('No active session found');
        }

        logger.info('VezgoCallback', 'Fetching account data', { 
          accountId 
        });
        
        // Create path parameter format for Vezgo API call
        const vezgoAccountsPath = `vezgo-get-accounts/${accountId}`;
        
        // Log what we're actually sending to help with debugging
        logger.debug('VezgoCallback', 'Account API request details', {
          path: vezgoAccountsPath,
          accountId,
          clientId: clientId || "not provided",
          timestamp: new Date().toISOString()
        });
        
        // Ensure valid token before making API call
        await ensureValidToken('vezgo-get-accounts');
        
        // Use path parameter as per Vezgo docs
        const { data: accountData, error: accountError } = await supabase.functions.invoke(
          vezgoAccountsPath,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              ...(clientId ? { 'x-client-id': clientId } : {})
            }
          }
        );

        if (accountError || !accountData) {
          throw new Error('Failed to fetch account data: ' + (accountError?.message || 'Unknown error'));
        }

        logger.info('VezgoCallback', 'Account data received', { 
          accountId,
          accountDataStructure: JSON.stringify(accountData),
          hasAccount: !!accountData.account
        });

        let accountToSave = null;
        
        if (accountData && accountData.account) {
          const vezgoAccount = accountData.account;
          
          // Validate required fields based on Vezgo API schema
          if (!vezgoAccount.id) {
            logger.error('VezgoCallback', 'Account data missing required id field', {
              accountId,
              availableFields: Object.keys(vezgoAccount)
            });
          } else {
            // Transform to our expected format
            accountToSave = {
              id: vezgoAccount.id,
              name: vezgoAccount.name || 'Vezgo Account',
              resource_type: vezgoAccount.resource_type,
              provider: {
                name: provider || 'vezgo',
                display_name: vezgoAccount.provider?.display_name || 'Vezgo',
                logo: vezgoAccount.provider?.logo || ''
              },
              fiat_value: vezgoAccount.fiat_value || '0',
              fiat_ticker: vezgoAccount.fiat_ticker || 'USD',
              // Include balances if available
              balances: Array.isArray(vezgoAccount.balances) ? vezgoAccount.balances.map(balance => ({
                ticker: balance.ticker || '',
                name: balance.name || balance.ticker || 'Unknown',
                asset_type: balance.asset_type || 'alternative',
                amount: balance.amount || '0',
                fiat_value: balance.fiat_value || '0',
                fiat_ticker: balance.fiat_ticker || 'USD'
              })) : [],
              // Include wallets if available
              wallets: Array.isArray(vezgoAccount.wallets) ? vezgoAccount.wallets : []
            };
            
            logger.info('VezgoCallback', 'Account data transformed successfully', {
              accountId,
              hasBalances: Array.isArray(vezgoAccount.balances) && (vezgoAccount.balances?.length || 0) > 0,
              balanceCount: Array.isArray(vezgoAccount.balances) ? (vezgoAccount.balances?.length || 0) : 0
            });
          }
        }
        
        if (!accountToSave) {
          logger.warn('VezgoCallback', 'No valid account data received from Vezgo API', {
            accountId,
            responseData: JSON.stringify(accountData)
          });
          return;
        }

        // CRITICAL FIX: Follow the same URL parameter prioritization approach for save-accounts
        const saveUrlParams = new URLSearchParams();
        saveUrlParams.append('vezgo_account_id', accountId);
        
        // Add client_id if available (in snake_case format per Vezgo docs)
        if (clientId) {
          saveUrlParams.append('client_id', clientId);
        }
        
        // Ensure consistent parameter naming in both URL and body
        const accountsBody = {
          // Include in both formats to ensure compatibility
          vezgo_account_id: accountId,
          vezgoAccountId: accountId,
          accounts: accountData?.account ? [accountData.account] : [],
          accountSyncTimestamp: new Date().toISOString(),
          provider,
          client_id: clientId // Include client_id if available
        };
        
        // Log what we're sending before the API call
        logger.debug('VezgoCallback', 'Save accounts API request details', {
          url: `vezgo-save-accounts?${saveUrlParams.toString()}`,
          bodyData: accountsBody,
          timestamp: new Date().toISOString()
        });
        
        logger.info('VezgoCallback', 'Sending accounts to save', { 
          accountsBody,
          accountId
        });
        
        // Ensure valid token before making API call
        await ensureValidToken('vezgo-save-accounts');
        
        const { data: saveAccountResult, error: saveAccountError } = await supabase.functions.invoke(
          `vezgo-save-accounts?${saveUrlParams.toString()}`,
          {
            // Explicitly stringify the body to ensure it's transmitted correctly
            method: 'POST',
            body: accountsBody,  // No JSON.stringify - let Supabase handle it
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Log response immediately for debugging
        logger.debug('VezgoCallback', 'Save accounts API response', {
          success: !saveAccountError,
          hasData: !!saveAccountResult,
          error: saveAccountError,
          timestamp: new Date().toISOString()
        });

        if (saveAccountError) {
          logger.error('VezgoCallback', 'Failed to save account data', {
            error: saveAccountError,
            accountId
          });
          throw new Error('Failed to save account data: ' + saveAccountError.message);
        }

        logger.info('VezgoCallback', 'Account data saved successfully', { 
          accountId,
          saveAccountResult
        });

        if (toastId) {
          toast.success('Connection established successfully', {
            id: toastId
          });
        }

        cleanupCallbackParams();

        const redirectParams = new URLSearchParams();
        redirectParams.set('success', 'true');
        redirectParams.set('account', accountId);

        setTimeout(() => {
          if (!didCancel) {
            navigate(`/investments/connect?${redirectParams.toString()}`, { replace: true });
          }
        }, 1500);

      } catch (err) {
        logger.error('VezgoCallback', 'Error during callback processing', {
          error: err instanceof Error ? err.message : String(err)
        });
        handleNavigationError(err);
        setError(err instanceof Error ? err.message : String(err));
        setShowRetry(true);
        if (toastId) {
          toast.error(`Connection error: ${err instanceof Error ? err.message : String(err)}`, {
            id: toastId
          });
        }
      } finally {
        setIsProcessing(false);
      }
    };

    const timeoutId = setTimeout(processCallback, 1000);

    return () => {
      didCancel = true;
      clearTimeout(timeoutId);
    };
  }, [navigate, vezgoNavigation, handleNavigationError, vezgoState]);

  const handleRetry = () => {
    CallbackProcessor.resetCallbackProcessingState();
    setShowRetry(false);
    setError(null);
    logger.info('VezgoCallback', 'User triggered retry; callback state reset');
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="p-6 max-w-md text-center">
        <div className="flex flex-col items-center">
          {!error ? (
            <Spinner size="lg" className="mb-4 text-gray-700" ariaLabel="Processing..." />
          ) : (
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
          )}

          <h1 className="text-xl font-semibold mb-2">
            {error ? 'Connection Error' : 'Processing connection...'}
          </h1>

          <p className="text-gray-500 mb-2">
            {error
              ? `There was an error processing your connection: ${error}`
              : 'Please wait while we complete your connection. This may take a moment.'}
          </p>

          {showRetry && (
            <button
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded shadow"
              onClick={handleRetry}
            >
              Retry Processing
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}

export default VezgoCallback;
